import React, { useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Datepicker,
  Select,
} from 'components';
import {
  createMaCisCreator,
  setMaCisCreateErrorsCreator,
} from '../../../../../store/actionsCreators';
import reducer, { defaultState, updateFieldCreator } from './reducer';
import { getUsersOptions } from '../../../../../api/services/user';
import DebouncedSelect from '../../../../../components/DebounceSelect/DebounceSelect';
import FileInput from '../../../../../components/FileInput/FileInput';
import { connect } from 'react-redux';
import moment from 'moment';
import { roleOptions, signatureTypeOptions } from '../../../../../shared/Cis/CisOptions';

const AddSignature = ({ errors, createSignature, removeError }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeFileNative = (name) => (e) => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: e.target.files && e.target.files.length ? e.target.files[0] : null,
      }),
    );
  };

  const handleClearFileNative = (name) => () => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: null,
      }),
    );
  };

  const handleChangeCustom = (name) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
  };

  const submit = () => {
    const formData = new FormData();
    formData.append('user', state.user);
    if (state.signature && state.signatureType === 'H') {
      formData.append('signature', state.signature);
    }
    if (state.dateFrom) {
      formData.append('dateFrom', moment(state.dateFrom).format('YYYY-MM-DD'));
    }
    formData.append('signatureType', state.signatureType);
    formData.append('assessorType', state.assessorType);

    createSignature(formData, history);
  };

  return (
    <PageContent>
      <Container>
        <Margin bottom="24">
          <Heading size="h5">{t('page.user.cis.signatures.addSignature')}</Heading>
        </Margin>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="user"
                label={t('page.user.cis.signatures.user')}
                error={errors.user}
                required
              >
                <DebouncedSelect
                  id="user"
                  value={state.user?.id}
                  onChange={handleChangeSelect('user', 'user')}
                  placeholder={t('page.user.cis.signatures.userPlaceholder')}
                  fetchOptions={getUsersOptions}
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="dateFrom"
                label={t('page.user.cis.signatures.dateFrom')}
                error={errors.dateFrom}
              >
                <Datepicker
                  id="dateFrom"
                  type="text"
                  small
                  placeholderText={t('page.user.cis.signatures.dateFromPlaceholder')}
                  selected={state.dateFrom}
                  onChange={handleChangeCustom('dateFrom', 'dateFrom')}
                  dateFormat="yyyy-MM-dd"
                  showYearDropdown
                  showMonthDropdown
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="signatureType"
                label={t('page.user.cis.signatures.signatureType')}
                required
                error={errors.signatureType}
              >
                <Select
                  id="signatureType"
                  type="text"
                  placeholder={t('page.user.cis.signatures.signatureTypePlaceholder')}
                  value={
                    signatureTypeOptions.find((option) => option.value === state.signatureType) ||
                    ''
                  }
                  options={signatureTypeOptions}
                  onChange={handleChangeSelect('signatureType', 'signatureType')}
                />
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="signature"
                label={t('page.user.cis.signatures.signature')}
                error={errors.signature}
                disabled={state.signatureType !== 'H'}
                required={state.signatureType === 'H'}
                description={t('global.uploadPngInfo')}
              >
                <FileInput
                  clear={handleClearFileNative('signature')}
                  onChange={handleChangeFileNative('signature')}
                  placeholder="Add a file"
                  fileName={state.signature ? state.signature.name || state.signature.fileName : ''}
                  disabled={state.signatureType !== 'H'}
                  accept=".png"
                />
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="assessorType"
                label={t('page.user.cis.signatures.role')}
                required
                error={errors.assessorType}
              >
                <Select
                  id="assessorType"
                  type="text"
                  placeholder={t('page.user.cis.signatures.rolePlaceholder')}
                  value={roleOptions.find((option) => option.value === state.assessorType) || ''}
                  options={roleOptions}
                  onChange={handleChangeSelect('assessorType', 'assessorType')}
                />
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom={{ xs: 12, md: 0 }}>
              <Button onClick={() => submit()}>{t('global.saveChangesButton')}</Button>
            </Margin>
          </Col>
        </Row>
      </Container>
    </PageContent>
  );
};

AddSignature.propTypes = {
  errors: PropTypes.object,
  createSignature: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
};

AddSignature.defaultProps = {
  errors: {},
};

const mapStateToProps = ({ ma }) => ({
  errors: ma.cis.signatures.createErrors || {},
});

const mapDispatchToProps = (dispatch) => ({
  createSignature: (params, history) => dispatch(createMaCisCreator('signatures', params, history)),
  removeError: (errors) => dispatch(setMaCisCreateErrorsCreator('signatures', errors)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddSignature);
